import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { sanityFetch, sanityImageUrl } from "../sanityClient";
import main_hall_1 from "../assets/main_hall/main-hall-1.jpg";
import main_hall_2 from "../assets/main_hall/main-hall-2.jpg";
import main_hall_3 from "../assets/main_hall/main-hall-3.jpg";
import main_hall_4 from "../assets/main_hall/main-hall-4.jpg";
import main_hall_5 from "../assets/main_hall/main-hall-5.jpg";

interface RentalRate {
  space: string;
  tablesChairsCapacity: number;
  standingCapacity: number;
  forProfitHalfDay: number;
  forProfitFullDay: number;
  nonProfitHalfDay: number;
  nonProfitFullDay: number;
}

interface SanityImage {
  asset: { _ref: string; _type: string };
  alt?: string;
}

interface MainMarketHallSettings {
  rentalYear?: string;
  additionalStaffRate?: number;
  electricalFeePerBooth?: number;
  rentalRates?: RentalRate[];
  images?: SanityImage[];
}

const FALLBACK_RATES: RentalRate[] = [
  { space: "Market Café", tablesChairsCapacity: 75, standingCapacity: 100, forProfitHalfDay: 424, forProfitFullDay: 605, nonProfitHalfDay: 242, nonProfitFullDay: 424 },
  { space: "Half-Hall (West)", tablesChairsCapacity: 200, standingCapacity: 300, forProfitHalfDay: 605, forProfitFullDay: 908, nonProfitHalfDay: 424, nonProfitFullDay: 605 },
  { space: "Half-Hall (East, excl. Hot Food)", tablesChairsCapacity: 200, standingCapacity: 300, forProfitHalfDay: 605, forProfitFullDay: 908, nonProfitHalfDay: 424, nonProfitFullDay: 605 },
  { space: "Half-Hall (East, incl. Hot Food)", tablesChairsCapacity: 300, standingCapacity: 400, forProfitHalfDay: 726, forProfitFullDay: 1029, nonProfitHalfDay: 484, nonProfitFullDay: 726 },
  { space: "Full Hall (excl. Hot Food)", tablesChairsCapacity: 400, standingCapacity: 600, forProfitHalfDay: 908, forProfitFullDay: 1210, nonProfitHalfDay: 605, nonProfitFullDay: 908 },
  { space: "Full Hall (incl. Hot Food)", tablesChairsCapacity: 500, standingCapacity: 700, forProfitHalfDay: 1029, forProfitFullDay: 1320, nonProfitHalfDay: 726, nonProfitFullDay: 1029 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const LightCard = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: "#ffffff", border: "1px solid rgba(45,106,20,0.12)", borderRadius: 16, padding: "1.75rem", boxShadow: "0 2px 20px rgba(0,0,0,0.055)", ...style }}>
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 800, color: "#0d1b2a", margin: 0 }}>
      {children}
    </h2>
    <div style={{ width: 56, height: 4, background: "linear-gradient(90deg, #6dc744, #4a8c25)", borderRadius: 2, marginTop: 8 }} />
  </div>
);

const NAV_ITEMS = [
  { label: "Gallery", href: "#gallery" },
  { label: "Event Types", href: "#event-types" },
  { label: "Food & Drink", href: "#food-beverage" },
  { label: "Pricing", href: "#pricing" },
  { label: "Amenities", href: "#amenities" },
  { label: "Book Now", href: "#book" },
];

const MainMarketHallDetails: React.FC = () => {
  const [settings, setSettings] = useState<MainMarketHallSettings | null>(null);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    sanityFetch<MainMarketHallSettings>(
      `*[_type == "mainMarketHall"][0]{rentalYear, additionalStaffRate, electricalFeePerBooth, rentalRates, images[]}`
    ).then((data) => { if (data) setSettings(data); });
  }, []);

  const rentalYear = settings?.rentalYear ?? "2026";
  const staffRate = settings?.additionalStaffRate ?? 18;
  const electricalFee = settings?.electricalFeePerBooth ?? 10;
  const rates = settings?.rentalRates?.length ? settings.rentalRates : FALLBACK_RATES;

  const FALLBACK_IMAGES = [main_hall_1, main_hall_2, main_hall_3, main_hall_4, main_hall_5];
  const images: string[] = settings?.images?.length
    ? settings.images.map((img) => sanityImageUrl(img.asset))
    : FALLBACK_IMAGES;

  return (
    <div style={{ fontFamily: "inherit" }}>

      {/* ── HERO ── */}
      <section style={{ position: "relative", minHeight: 520, display: "flex", alignItems: "center", overflow: "hidden" }}>
        <img
          src={images[0]}
          alt="Main Market Hall"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,38,5,0.88) 0%, rgba(13,27,42,0.82) 55%, rgba(21,3,8,0.78) 100%)" }} />
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "5rem 1.5rem 6rem", position: "relative", zIndex: 1, width: "100%" }}>
          <motion.div variants={stagger} initial="hidden" animate="visible" style={{ maxWidth: 640 }}>
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Rental Venue
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              Main{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Market Hall
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.75)", lineHeight: 1.7, marginBottom: "2rem" }}>
              A dynamic, adaptable venue at the heart of the farmers' market — perfect
              for dinners, performances, conferences, and community gatherings. Fully
              accessible with AV support and optional café services.
            </motion.p>

            <motion.div variants={fadeUp} style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a
                href="#book"
                style={{ display: "inline-block", background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", textDecoration: "none", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
              >
                Inquire About Rental →
              </a>
              <a
                href="#pricing"
                style={{ display: "inline-block", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", textDecoration: "none", transition: "background 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
              >
                View Pricing
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── STICKY NAV ── */}
      <div style={{ position: "sticky", top: 65, zIndex: 90, background: "rgba(255,253,248,0.95)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(45,106,20,0.12)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", display: "flex", gap: "0.25rem", overflowX: "auto", scrollbarWidth: "none" }}>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              style={{ display: "inline-block", padding: "0.9rem 1.1rem", fontSize: "0.88rem", fontWeight: 600, color: "#2d6a14", textDecoration: "none", borderBottom: "2px solid transparent", whiteSpace: "nowrap", transition: "color 0.2s, border-color 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#c6252d"; e.currentTarget.style.borderBottomColor = "#c6252d"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#2d6a14"; e.currentTarget.style.borderBottomColor = "transparent"; }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>

      {/* ── PHOTO GALLERY ── */}
      <section id="gallery" style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Photo Gallery</SectionHeading>
            </motion.div>

            {isMobile ? (
              /* ── Mobile gallery: full-width hero + 2×2 grid ── */
              <motion.div variants={fadeUp} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ width: "100%", aspectRatio: "16/9", overflow: "hidden", borderRadius: 12, cursor: "pointer" }} onClick={() => setLightboxImg(images[0])}>
                  <img src={images[0]} alt="Main Hall" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                  {images.slice(1).map((src, i) => (
                    <div key={i} style={{ aspectRatio: "4/3", overflow: "hidden", borderRadius: 12, cursor: "pointer" }} onClick={() => setLightboxImg(src)}>
                      <img src={src} alt={`Hall ${i + 2}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              /* ── Desktop gallery: masonry-style 12-column grid ── */
              <motion.div
                variants={fadeUp}
                style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gridTemplateRows: "240px 240px", gap: "0.75rem" }}
              >
                <div style={{ gridColumn: "1 / 7", gridRow: "1 / 3", overflow: "hidden", borderRadius: 16, cursor: "pointer" }} onClick={() => setLightboxImg(images[0])}>
                  <img src={images[0]} alt="Main Hall" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                    onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                    onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                </div>
                {[images[1], images[2]].map((src, i) => (
                  <div key={i} style={{ gridColumn: `${7 + i * 3} / ${10 + i * 3}`, gridRow: "1 / 2", overflow: "hidden", borderRadius: 16, cursor: "pointer" }} onClick={() => setLightboxImg(src)}>
                    <img src={src} alt={`Hall ${i + 2}`} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                  </div>
                ))}
                {[images[3], images[4]].map((src, i) => (
                  <div key={i} style={{ gridColumn: `${7 + i * 3} / ${10 + i * 3}`, gridRow: "2 / 3", overflow: "hidden", borderRadius: 16, cursor: "pointer" }} onClick={() => setLightboxImg(src)}>
                    <img src={src} alt={`Hall ${i + 4}`} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                  </div>
                ))}
              </motion.div>
            )}
            <motion.p variants={fadeUp} style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "0.75rem", textAlign: "center" }}>
              Click any photo to enlarge
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          onClick={() => setLightboxImg(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", cursor: "zoom-out" }}
        >
          <img src={lightboxImg} alt="Enlarged" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 12, boxShadow: "0 8px 64px rgba(0,0,0,0.5)", objectFit: "contain" }} />
          <button onClick={() => setLightboxImg(null)} style={{ position: "absolute", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, color: "white", fontSize: "1.3rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
      )}

      {/* ── EVENT TYPES ── */}
      <section id="event-types" style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Types of Events</SectionHeading>
              <p style={{ color: "#4a5568", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: 640, margin: "-1rem 0 2rem" }}>
                Our Main Market Hall accommodates a wide variety of events. Food-focused and
                community-building events are given priority.
              </p>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.25rem" }}>
              {[
                { icon: "🍽️", title: "Dinners & Banquets", desc: "Elegant sit-down meals for up to 500 guests with flexible table arrangements." },
                { icon: "🎶", title: "Performances & Concerts", desc: "Live music, comedy shows, and cultural performances in a vibrant setting." },
                { icon: "🎭", title: "Rehearsals & Productions", desc: "Art productions, theatre rehearsals, and creative events." },
                { icon: "📊", title: "Conferences & Meetings", desc: "Community gatherings, trade shows, and corporate events with AV support." },
                { icon: "🛒", title: "Markets & Exhibitions", desc: "Vendor markets, artisan fairs, and pop-up exhibitions." },
                { icon: "✨", title: "Something Unique?", desc: "Have a vision we haven't listed? Contact our Events Manager to make it happen.", link: "mailto:eventsmanager@sjfm.ca" },
              ].map(({ icon, title, desc, link }, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <LightCard style={{ borderTop: "4px solid #6dc744", height: "100%" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{icon}</div>
                    <h3 style={{ margin: "0 0 0.5rem", fontWeight: 800, fontSize: "1rem", color: "#0d1b2a" }}>{title}</h3>
                    <p style={{ margin: 0, color: "#4a5568", fontSize: "0.92rem", lineHeight: 1.6 }}>
                      {link ? <><span>{desc.split("Contact")[0]}</span><a href={link} style={{ color: "#c6252d", fontWeight: 600 }}>Contact our Events Manager</a></> : desc}
                    </p>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOD & BEVERAGE ── */}
      <section id="food-beverage" style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Food & Beverage</SectionHeading>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
              {[
                { icon: "☕", title: "Market Café", accent: "#C19A3B", bg: "#fdf6e3", content: <>If you'd like the café open during your event for coffee and snacks, contact <em>Cheddar's Café by Oh My Cheeses</em> at <a href="mailto:ohmycheesesnl@gmail.com" style={{ color: "#c6252d", fontWeight: 600 }}>ohmycheesesnl@gmail.com</a>.</> },
                { icon: "🍷", title: "Alcohol Policy", accent: "#c6252d", bg: "#fdecea", content: <>The SJFM does not hold a permanent liquor license. If you'd like to serve alcohol, please review our licensing requirements with the Events Manager.</> },
                { icon: "🔥", title: "Hot Food Area", accent: "#e84545", bg: "#fdecea", content: <>Vendors in the Hot Food Area operate from semi-permanent booths and cannot be removed for events — plan your layout accordingly.</> },
                { icon: "🏛️", title: "Hall Setup", accent: "#6dc744", bg: "#f2fbee", content: <>The rest of the Market Hall can be fully emptied and arranged to suit your needs — we work with you on the layout.</> },
                { icon: "🍵", title: "Coffee & Tea Service", accent: "#C19A3B", bg: "#fdf6e3", content: <>Coffee and tea service is available for events. Ask us about current pricing when you inquire.</> },
                { icon: "🥗", title: "Catering Options", accent: "#6dc744", bg: "#f2fbee", content: <>Renters may bring their own food. Alternatively, many Market Vendors can cater — from snacks to full meals and desserts. Ask us for a list.</> },
              ].map(({ icon, title, accent, bg, content }, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <LightCard style={{ background: bg, borderTop: `4px solid ${accent}`, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.25rem" }}>
                      <span style={{ fontSize: "1.6rem" }}>{icon}</span>
                      <h3 style={{ margin: 0, fontWeight: 800, fontSize: "1rem", color: "#0d1b2a" }}>{title}</h3>
                    </div>
                    <p style={{ margin: 0, color: "#4a5568", fontSize: "0.92rem", lineHeight: 1.65 }}>{content}</p>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>{rentalYear} Rental Rates</SectionHeading>
              <p style={{ color: "#4a5568", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: 600, margin: "-1rem 0 2rem" }}>
                All rates include one staff member on-site. HST applies to all totals.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} style={{ overflowX: "auto", borderRadius: 16, boxShadow: "0 2px 24px rgba(0,0,0,0.07)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: "white", fontSize: "0.9rem", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #0a2605, #0d1b2a)" }}>
                    {["Space", "Tables & Chairs", "Standing", "For-Profit Half Day", "For-Profit Full Day", "Non-Profit Half Day", "Non-Profit Full Day"].map((h) => (
                      <th key={h} style={{ padding: "1rem 1.1rem", color: "white", fontWeight: 700, fontSize: "0.82rem", textAlign: "left", whiteSpace: "nowrap", letterSpacing: "0.03em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rates.map((rate, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f9fdf7", borderBottom: "1px solid rgba(45,106,20,0.08)", transition: "background 0.15s" }}
                      onMouseOver={(e) => (e.currentTarget.style.background = "#f0faea")}
                      onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f9fdf7")}
                    >
                      <td style={{ padding: "0.9rem 1.1rem", fontWeight: 700, color: "#0d1b2a", whiteSpace: "nowrap" }}>{rate.space}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#4a5568" }}>{rate.tablesChairsCapacity}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#4a5568" }}>{rate.standingCapacity}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#2d6a14", fontWeight: 600 }}>${rate.forProfitHalfDay}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#2d6a14", fontWeight: 600 }}>${rate.forProfitFullDay}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#6b4f1a", fontWeight: 600 }}>${rate.nonProfitHalfDay}</td>
                      <td style={{ padding: "0.9rem 1.1rem", color: "#6b4f1a", fontWeight: 600 }}>${rate.nonProfitFullDay}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem", marginTop: "1.5rem" }}>
              {[
                { label: "Half Day", value: "4 hours including setup & teardown" },
                { label: "Full Day", value: "4+ hours" },
                { label: "Additional Staff", value: `$${staffRate}/hr per person` },
                { label: "Electrical (per booth)", value: `$${electricalFee} flat fee` },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: "white", borderRadius: 12, padding: "1rem 1.25rem", border: "1px solid rgba(45,106,20,0.12)", display: "flex", gap: "0.75rem", alignItems: "center" }}>
                  <span style={{ width: 8, height: 8, background: "#6dc744", borderRadius: "50%", flexShrink: 0 }} />
                  <span style={{ color: "#0d1b2a", fontSize: "0.92rem" }}><strong>{label}:</strong> {value}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── AMENITIES ── */}
      <section id="amenities" style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Venue Amenities</SectionHeading>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.1rem" }}>
              {[
                { icon: "♿", title: "Fully Accessible", desc: "11 gender-neutral washrooms and barrier-free access throughout." },
                { icon: "📶", title: "Free Wi-Fi", desc: "Public Wi-Fi available at no cost for you and your guests." },
                { icon: "🎤", title: "AV Equipment", desc: "Microphone and speaker system available on request — ideal for small events and PA use." },
                { icon: "🪑", title: "Seating", desc: "150 chairs on-site. Additional chairs can be arranged by the renter." },
                { icon: "🏧", title: "On-Site ATMs", desc: "Two ATMs available. ATM fees support further SJFM development." },
                { icon: "⚡", title: "Electrical Access", desc: `Flat fee of $${electricalFee}/booth for trade show and booth-type events.` },
                { icon: "🍳", title: "Commercial Kitchen Add-on", desc: "Ask us about adding the Commercial Kitchen to your rental for catering needs." },
                { icon: "🏢", title: "Non-Profit Venue", desc: "SJFM is a non-profit that rents this building from the City of St. John's." },
                { icon: "💰", title: "HST Applied", desc: "HST is added to all invoice totals as required." },
              ].map(({ icon, title, desc }, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <LightCard style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "1.75rem", flexShrink: 0, lineHeight: 1 }}>{icon}</span>
                    <div>
                      <h3 style={{ margin: "0 0 0.3rem", fontWeight: 800, fontSize: "0.95rem", color: "#0d1b2a" }}>{title}</h3>
                      <p style={{ margin: 0, color: "#4a5568", fontSize: "0.88rem", lineHeight: 1.6 }}>{desc}</p>
                    </div>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STAFF & BOOKING CTA ── */}
      <section id="book" style={{ background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)", padding: "5rem 1.5rem", position: "relative", overflow: "hidden", scrollMarginTop: 120 }}>
        <div style={{ position: "absolute", top: -80, right: -80, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "3rem", alignItems: "start" }}>

              {/* Staff info */}
              <div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1.25rem" }}>
                  <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
                  Staff Support
                </span>
                <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 900, color: "white", margin: "0 0 1.25rem" }}>
                  We're Here to Help
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
                  {[
                    { label: "Included", text: "All rentals include one SJFM staff member on-site for the duration of your event." },
                    { label: "Extra Staff", text: "Additional staff may be required depending on event size — discussed before signing." },
                    { label: "Staffing Rate", text: `Extra staff are billed at $${staffRate}/hr per person.` },
                  ].map(({ label, text }) => (
                    <div key={label} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                      <span style={{ width: 8, height: 8, background: "#6dc744", borderRadius: "50%", flexShrink: 0, marginTop: 7 }} />
                      <p style={{ margin: 0, color: "rgba(255,255,255,0.8)", lineHeight: 1.65, fontSize: "0.97rem" }}>
                        <strong style={{ color: "white" }}>{label}:</strong> {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking card */}
              <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: "2.5rem", backdropFilter: "blur(8px)" }}>
                <h2 style={{ fontSize: "1.7rem", fontWeight: 900, color: "white", margin: "0 0 0.75rem" }}>
                  Ready to Book?
                </h2>
                <p style={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", fontSize: "1rem" }}>
                  Reach out to our Events Manager to check availability, discuss your
                  needs, and get a custom quote for your event.
                </p>
                <a
                  href="mailto:eventsmanager@sjfm.ca"
                  style={{ display: "block", textAlign: "center", background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", borderRadius: 999, padding: "1rem 2rem", fontWeight: 700, fontSize: "clamp(0.75rem, 3.5vw, 1rem)", textDecoration: "none", boxShadow: "0 4px 20px rgba(198,37,45,0.4)", transition: "transform 0.2s, box-shadow 0.2s", marginBottom: "1rem", whiteSpace: "nowrap" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(198,37,45,0.55)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(198,37,45,0.4)"; }}
                >
                  eventsmanager@sjfm.ca →
                </a>
                <p style={{ margin: 0, textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "0.82rem" }}>
                  We typically respond within 1–2 business days
                </p>
              </div>

            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default MainMarketHallDetails;
